import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Mail, Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { auth } from "@/lib/firebase";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import BrandMark from "@/components/BrandMark";
import { API } from "@/App";

const googleProvider = new GoogleAuthProvider();

// After auth, route hosts into the Host Hub (or finish onboarding); players honour returnTo.
const resolveDestination = async (explicitReturn) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    const res = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const me = await res.json();
      if (me.is_host) return me.onboarding_complete ? "/host" : "/onboarding";
    }
  } catch { /* ignore, fall back */ }
  return explicitReturn && explicitReturn !== "/" ? explicitReturn : "/";
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  // Accept return URL via state (in-app nav) OR ?redirect= query param (external/email links)
  const searchParams = new URLSearchParams(location.search);
  const returnTo = location.state?.from || searchParams.get("redirect") || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (loading) return;
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      toast.success("Welcome back!");
      const dest = await resolveDestination(returnTo);
      setTimeout(() => navigate(dest, { replace: true }), 100);
    } catch (error) {
      const code = error.code;
      if (code === "auth/user-not-found" || code === "auth/invalid-credential") {
        toast.error("Invalid email or password.");
      } else if (code === "auth/wrong-password") {
        toast.error("Wrong password. Please try again.");
      } else if (code === "auth/too-many-requests") {
        toast.error("Too many attempts. Please try again later.");
      } else if (code === "auth/invalid-email") {
        toast.error("Invalid email address.");
      } else {
        toast.error(error.message || "Login failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      toast.success("Welcome back!");
      const dest = await resolveDestination(returnTo);
      setTimeout(() => navigate(dest, { replace: true }), 100);
    } catch (error) {
      if (error.code === "auth/popup-closed-by-user") return;
      if (error.code === "auth/unauthorized-domain") {
        toast.error("This domain is not authorized for Google login. Please contact support.");
      } else {
        toast.error("Google sign-in failed. Please try again.");
      }
      console.error("Google login error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="login-page">
      <header className="p-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 pb-8">
        <div className="w-full max-w-sm space-y-8">
          <div className="text-center">
            <div className="mx-auto mb-4 inline-flex">
              <BrandMark size="lg" showWordmark={false} />
            </div>
            <h1 className="font-publica text-3xl mb-2">Welcome back</h1>
            <p className="text-muted-foreground">Sign in to find your next game</p>
          </div>

          <Button
            variant="outline"
            className="w-full h-12 gap-3 border-border hover:bg-muted"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>

          <div className="relative">
            <Separator className="my-4" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-xs text-muted-foreground">
              or continue with email
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12 bg-muted/50 border-border input-glow" data-testid="email-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="password" type={showPassword ? "text" : "password"} placeholder="Min. 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 pr-10 h-12 bg-muted/50 border-border input-glow" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full h-12 btn-glow bg-primary text-primary-foreground hover:bg-primary/90 font-display text-lg" disabled={loading} data-testid="login-submit-btn">
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : "Sign In"}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/signup" state={{ from: returnTo }} className="text-primary-ink hover:text-primary-ink hover:underline font-medium" data-testid="signup-link">Sign up</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
